export type Agent = {
  id: string;
  type: string;
  name: string;
  description: string;
  customGPTs: string;
  created_by?: string;
  created_by_name?: string;
  created_at?: string;
  updated_at?: string;
}

export type FrontendSettings = {
  feature_flags: {
    tabular_code_interpreter: boolean;
    [key: string]: boolean | number;
  };
}

export type GroupParticipant = {
  userId: string;
  name: string;
  email: string;
}

export type GroupConversation = {
  id: string;
  title: string;
  ownerId: string;
  ownerName: string;
  participants: GroupParticipant[];
  deployment: string;
  customGPTID: string;
  date: string;
  messages: ChatMessage[];
}

export type AskResponse = {
  answer: string;
  citations: Citation[];
  error?: string;
  message_id?: string;
  feedback?: Feedback;
  model?: string;
  export_text?: string;
  export_citations?: any[];
};

export type Category = {
  id: number;
  name: string;
  description: string;
  customGPTs?: any[];
};

export enum ConversationResponseObjectType {
  Conversation = "chat.completion",
  ConversationChunk = "chat.completion.chunk"
}

export type ChatMessage = {
  id: string;
  role: string;
  content: string;
  date: string;
  userId?: string;
  feedback?: Feedback;
  context?: string | FileContent[];
  tool_type?: "web_search" | "code_interpreter" | string;
  protectedContext?: ProtectedTabularDescriptor[];
  model?: string;
  summary_index?: number;
  selection_index?: number;
  files?: string;
  filename?: string;
  direct_files?: boolean;
  publicImage?: boolean;
  senderName?: string;
  error_code?: string;
  error_type?: string;
  error_details?: StreamErrorDetails;
  status?: StreamStatus;
  /** Wire-format timestamp for non-dialogue stream events. */
  createdAt?: string;
  /** Prompt message this system event belongs after in the conversation. */
  anchorMessageId?: string;
};

export type StreamStatus = {
  kind: "compacting";
  message: string;
} | {
  kind: "clear";
};

export type PresentationAsset = {
  id: string;
  conversationId: string;
  parentMessageId: string;
  status: "starting" | "creating" | "ready" | "failed";
  filename: string;
  blobName: string;
  createdAt: string;
  updatedAt?: string;
  error?: string;
  error_details?: StreamErrorDetails;
};

export type StreamErrorDetails = {
  reason?: string;
  error_type?: string;
  error_code?: string;
  status_code?: number;
  upstream_code?: string;
  upstream_status_code?: number;
  trace_id?: string;
  conversation_id?: string;
  model?: string;
  reasoning?: string;
  upstream_id?: string;
  upstream_detail?: string;
  time?: string;
};

export type ErrorResponsePayload = {
  error: string;
  error_code?: string;
  error_type?: string;
  error_details?: StreamErrorDetails;
  limit?: number;
  week?: string;
}

export type ChatModel = {
  id: string;
  default: boolean;
  folder: string;
  brand: string;
  provider_api_family: string;
  params_format?: string;
  reasoning: string;
  name: string;
  deployment: string;
  files_allowed: boolean;
  images_allowed: boolean;
  reasoning_levels?: string[];
}

export type RealtimeVoiceSessionStatus = "idle" | "connecting" | "listening" | "speaking" | "error" | "ended";

export type VoiceSpeedLevel = "slow" | "normal" | "fast";

export type RealtimeToolInvocation = {
  call_id?: string;
  tool_name: string;
  arguments: Record<string, any>;
  status: "running" | "complete" | "error";
  error?: string | null;
  error_details?: StreamErrorDetails;
};

export type RealtimeVoiceSessionState = {
  status: RealtimeVoiceSessionStatus;
  localTranscript: string;
  assistantTranscript: string;
  activeToolCall: RealtimeToolInvocation | null;
  isMuted: boolean;
  error: string | null;
  error_details?: StreamErrorDetails;
};

export type RealtimeAssistantContext = {
  citations?: Citation[];
};

export type RealtimePrepareTurnResponse = {
  response_instructions: string;
  citations: Citation[];
  has_rag_context: boolean;
  custom_gpt_id: string;
};

export type RealtimeClientSecretResponse = {
  client_secret: {
    value: string;
    expires_at?: number;
    expiresAt?: number;
  };
  session: Record<string, any>;
  deployment: string;
  webrtc_url: string;
  context: {
    custom_gpt_id: string;
    agent_id: string;
    selected_tool: string;
  };
};

export type PersistedRealtimeTurnResponse = {
  conversation: Conversation;
  messages: ChatMessage[];
};
export type ConversationResponseEnvelope = {
  id: string;
  model: string;
  created: number;
  object: ConversationResponseObjectType | string;
  choices: ConversationResponseChoice[];
  summary_index?: number;
  selection_index?: number;
  history_metadata: {
    conversation_id: string;
    title: string;
    date: string;
  }
  url?: string;
  error?: any;
}

export type ConversationResponseChoice = {
  messages: ChatMessage[];
}

export type ChatResponse = ConversationResponseEnvelope;
export type ChatResponseChoice = ConversationResponseChoice;

export type Citation = {
  source?: string;
  id: string;
  title: string | null;
  filepath: string | null;
  content: string;
  text?: string | null;
  url: string | null;
  sas_url: string | null;
  metadata: string | null;
  type?: string | null;
  url_citation?: { title: string; url: string; favicon_url?: string | null; } | null;
}

export type Conversation = {
  id: string;
  title: string;
  model: ChatModel | null;
  reasoning?: string;
  customGPTID: string;
  selected_tool?: string | null;
  messages: ChatMessage[];
  presentations?: PresentationAsset[];
  date: string;
  pinned?: number;
  files?: string;
  filename?: string;
  direct_files?: boolean;
  migrated: boolean;
  shared: number;
  isGroup?: boolean;
  participants?: GroupParticipant[];
  ownerId?: string;
  ownerName?: string;
}

export type ConversationRequest = {
  messages: ChatMessage[];
};

export type PersonalizationTone = 'default' | 'professional' | 'friendly' | 'candid' | 'quirky' | 'efficient';

export type Personalization = {
  nickname: string;
  occupation: string;
  tone: PersonalizationTone;
  custom_instructions: string;
};

export const DEFAULT_PERSONALIZATION: Personalization = {
  nickname: '',
  occupation: '',
  tone: 'default',
  custom_instructions: '',
};

export type CurrentUser = {
  id: string;
  name: string;
  email: string;
  photo?: string;
}

export type SharepointSyncDisabledReason =
  | ''
  | 'not_a_document_library'
  | 'nested_under_other_library'
  | 'folder_gone'
  | 'invalid_url'
  // Set when cleanup after a library removal fails partway. Its record is disabled but
  // no longer configured, so it is filtered out of the editor's warnings rather than
  // given copy of its own.
  | 'removal_cleanup_failed';

export type SharepointLibraryStatus = {
  sharepointURL: string;
  syncDisabled: boolean;
  syncDisabledReason: SharepointSyncDisabledReason;
  syncDisabledDetail: string;
  syncDisabledAt: string;
}

export type CustomGPT = {
  id: string;
  image_path?: string;
  name: string;
  description: string;
  category?: number;
  system_prompt?: string;
  conv_starter1?: string;
  conv_starter2?: string;
  conv_starter3?: string;
  conv_starter4?: string;
  default_model?: string;
  has_knowledge?: boolean;
  sharepointURL?: string;
  sharepointURLs?: string[];
  sharepointArticles?: string;
  top_n?: number;
  files_downloadable?: number;
  visibility: number;
  ready: boolean;
  rating?: number;
  rating_count?: number;
  conversation_count?: number;
  created_by?: string;
  created_by_name?: string;
  created_at?: string;
  co_creator?: string;
  co_creator_name?: string;
  updated_at?: string;
  pinned?: boolean;
}

export type ErrorMessage = {
  title: string,
  subtitle: string
}

export enum Feedback {
  Neutral = "neutral",
  Positive = "positive",
  Negative = "negative",
  MissingCitation = "missing_citation",
  WrongCitation = "wrong_citation",
  OutOfScope = "out_of_scope",
  InaccurateOrIrrelevant = "inaccurate_or_irrelevant",
  OtherUnhelpful = "other_unhelpful",
  HateSpeech = "hate_speech",
  Violent = "violent",
  Sexual = "sexual",
  Manipulative = "manipulative",
  OtherHarmful = "other_harmlful"
}

export type IndividualFileChat = {
  id: string;
  filename: string;
  imageEditing: boolean;
  sas_url?: string | null;
  advancedAvailable?: boolean;
}

export type ProcessingMode = 'tabular_map';

export type ProtectedTabularDescriptor = {
  filename: string;
  kind: 'tabular_map';
};

export interface FileContent {
  filename: string;
  content: string;
  tokens?: number;
  processing_mode?: ProcessingMode;
  // Absent for tabular files and inline placeholders, which have no richer
  // extraction pass to offer.
  advanced_available?: boolean;
}

export type FileUploadResponse = {
  conversation_id: string;
  files_str: string;
  use_direct_context: boolean;
  effective_model?: string;
  filename?: string;
  plain_text?: string;
  total_tokens?: number;
  file_contents?: FileContent[];
  res?: {
    statusQueryGetUri?: string;
    terminatePostUri?: string;
  };
};

export type LibraryImage = {
  id: string;
  conversationId: string;
  messageId: string;
  prompt: string;
  imageUrl: string;
  addedByName: string;
  createdAt: string;
  userId: string;
}

export type Notification = {
  id?: string | '';
  notificationId?: string | '';
  title?: string | '';
  message?: string | '';
  subMessage?: string | '';
  color?: string | '';
  effectivedate?: string | '';
}

export type SearchConversation = {
  id: string;
  title: string;
  pinned?: number;
  date: string;
}

export type ToolMessageContent = {
  citations: Citation[];
  intent?: string | '';
}

export type UserInfo = {
  access_token: string;
  expires_on: string;
  id_token: string;
  provider_name: string;
  user_claims: any[];
  user_id: string;
};

export type SlideColumn = { heading: string; bullet_points: string[] };
export type SlideTable = { headers: string[]; rows: string[][] };
export type Slide = {
  heading: string;
  subtitle?: string;
  bullet_points?: (string | string[] | SlideColumn)[];
  table?: SlideTable;
  key_message?: string;
  img_keywords?: string;
};
export type SlideStructure = { title: string; subtitle?: string; slides: Slide[] };

export type WeeklyUsage = {
  used: number;
  input: number;
  output: number;
  reserved?: number;
  limit: number;
  week: string;
};

export type ProfileAnalyticsWeek = {
  week: string;
  totalTokens: number;
};

export type ProfileAnalytics = {
  lifetimeTokens: number;
  peakWeek: ProfileAnalyticsWeek | null;
  longestChatTokens: number;
  weekly: ProfileAnalyticsWeek[];
};

